import React from 'react';
import { 
  MapPin, 
  Phone, 
  Globe, 
  Clock, 
  Star,
  Building2,
  CheckCircle,
  Mail,
  Youtube,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Camera,
  FileText,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Button } from '@vaiu/ui/button';
import { Separator } from '@vaiu/ui/separator';

interface MerchantProfilePreviewProps {
  businessInfo?: {
    name?: string;
    description?: string;
    address?: string;
    phone?: string;
    website?: string;
    email?: string;
    hours?: any[];
    rating?: number;
    reviewCount?: number;
    photos?: string[];
    logo?: string;
  };
  socialMedia?: {
    youtube?: string;
    instagram?: string;
    facebook?: string;
    tiktok?: string;
    twitter?: string;
    linkedin?: string;
    wordpress?: string;
  };
  isMinimized?: boolean;
  onToggleSize?: () => void;
}

const MerchantProfilePreview: React.FC<MerchantProfilePreviewProps> = ({
  businessInfo = {},
  socialMedia = {},
  isMinimized = false,
  onToggleSize
}) => {
  const hasBusinessInfo = businessInfo.name || businessInfo.address || businessInfo.phone;
  const hasSocialMedia = Object.values(socialMedia).some(v => v && v.trim());
  
  const socialLinks = [
    { platform: 'youtube', icon: Youtube, color: 'hover:text-red-600' },
    { platform: 'instagram', icon: Instagram, color: 'hover:text-pink-600' },
    { platform: 'facebook', icon: Facebook, color: 'hover:text-blue-600' },
    { platform: 'tiktok', icon: Camera, color: 'hover:text-black' },
    { platform: 'twitter', icon: Twitter, color: 'hover:text-blue-400' },
    { platform: 'linkedin', icon: Linkedin, color: 'hover:text-blue-700' },
    { platform: 'wordpress', icon: FileText, color: 'hover:text-blue-500' },
  ];

  // Minimized view - just a compact card
  if (isMinimized) {
    return (
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {businessInfo.name || 'Your Business Name'}
                </h3>
                <p className="text-sm text-gray-600">
                  Live preview of your public profile
                </p>
              </div>
            </div>
            {onToggleSize && (
              <Button variant="outline" size="sm" onClick={onToggleSize}>
                Expand Preview
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full preview
  return (
    <Card className="overflow-hidden bg-gradient-to-b from-white to-gray-50 border-2 border-blue-200 shadow-lg">
      {/* Header with gradient */}
      <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-500" />
      
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 text-sm text-blue-600 font-medium mb-2">
            <Star className="w-4 h-4 fill-current" />
            Live Preview - This is how customers will see your profile
          </div>
          {onToggleSize && (
            <Button variant="ghost" size="sm" onClick={onToggleSize}>
              Minimize
            </Button>
          )}
        </div>

        {/* Business Header */}
        <div className="flex items-start gap-4">
          {businessInfo.logo || businessInfo.photos?.[0] ? (
            <img
              src={businessInfo.logo || businessInfo.photos?.[0]}
              alt={businessInfo.name || 'Business'}
              className="w-16 h-16 rounded-lg object-cover border-2 border-gray-200"
            />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
          )}
          
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {businessInfo.name || <span className="text-gray-400">Business Name</span>}
                </h2>
                {businessInfo.description && (
                  <p className="text-sm text-gray-600 mt-1">
                    {businessInfo.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              </div>
            </div>

            {/* Rating */}
            {businessInfo.rating && (
              <div className="flex items-center gap-2 mt-2">
                <div className="flex items-center">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="ml-1 text-sm font-medium">{businessInfo.rating}</span>
                </div>
                {businessInfo.reviewCount && (
                  <span className="text-xs text-gray-500">
                    ({businessInfo.reviewCount} reviews from Google)
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {hasBusinessInfo && (
          <>
            <Separator className="mb-4" />
            
            {/* Contact Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {businessInfo.phone && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span>{businessInfo.phone}</span>
                </div>
              )}
              
              {businessInfo.email && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{businessInfo.email}</span>
                </div>
              )}
              
              {businessInfo.website && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Globe className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{businessInfo.website}</span>
                </div>
              )}
              
              {businessInfo.address && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{businessInfo.address}</span>
                </div>
              )}
            </div>

            {/* Business Hours */}
            {businessInfo.hours && businessInfo.hours.length > 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Business Hours</span>
                </div>
                <div className="space-y-1">
                  {businessInfo.hours.slice(0, 2).map((hour, idx) => (
                    <div key={idx} className="text-xs text-gray-600">
                      {hour.day && hour.text && `${hour.day}: ${hour.text}`}
                    </div>
                  ))}
                  {businessInfo.hours.length > 2 && (
                    <div className="text-xs text-gray-400">
                      +{businessInfo.hours.length - 2} more...
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Social Media Links */}
        {hasSocialMedia && (
          <>
            <Separator className="my-4" />
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Connect With Us</p>
              <div className="flex gap-3">
                {socialLinks.map(({ platform, icon: Icon, color }) => {
                  const url = socialMedia[platform as keyof typeof socialMedia];
                  if (!url) return null;
                  
                  return (
                    <div
                      key={platform}
                      className={`text-gray-500 ${color} transition-colors cursor-pointer`}
                      title={platform}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Empty State */}
        {!hasBusinessInfo && !hasSocialMedia && (
          <div className="py-8 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">
              Your profile preview will appear here as you add information
            </p>
          </div>
        )}

        {/* Photos Strip */}
        {businessInfo.photos && businessInfo.photos.length > 0 && (
          <>
            <Separator className="my-4" />
            <div className="flex gap-2 overflow-x-auto pb-2">
              {businessInfo.photos.slice(0, 4).map((photo, idx) => (
                <img
                  key={idx}
                  src={photo}
                  alt={`Photo ${idx + 1}`}
                  className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                />
              ))}
              {businessInfo.photos.length > 4 && (
                <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm text-gray-500">+{businessInfo.photos.length - 4}</span>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MerchantProfilePreview;
